/**
 * 画布编辑器的新手引导。
 *
 * 为什么需要它：画布上最省事的几个动作都是「敲出来的」——敲 / 插块、双击改字、
 * 表格右键。这些在界面上一个字都看不见，用户不可能猜到。功能做了等于没做。
 *
 * 为什么不做成分步高亮浮层：那种引导要挡住半个界面，用户第二次进来只想干活，
 * 而且一旦布局改了就会指错地方。这里用两层轻的：
 *   常驻提示条 —— 一行字待在画布上方，任何时候扫一眼就想起来（主力）
 *   引导弹窗   —— 首次自动弹一次，之后可从「怎么用」重看（补全细节）
 *
 * 首屏漏斗数据说明流失发生在「打开了但没印出东西」，所以每一条都写成
 * 「你要做什么 → 怎么做」，不解释概念。
 */
import { Button, Modal, Typography } from 'antd';
import { useEffect, useState } from 'react';

/**
 * 帮助文档（飞书）。留空 = 引导里不显示这个入口。
 *
 * 文档已经写好并建在飞书里，但当前是「组织内可阅读」—— 客户在别的企业，
 * 点进去只会看到无权限页。放开到「互联网获得链接的人可阅读」之后再把地址
 * 填进来，这里是唯一需要改的地方：
 *   https://my.feishu.cn/docx/Q990dh7h6odNx5xgUaacvFPXnnf
 */
export const HELP_DOC_URL = 'https://my.feishu.cn/wiki/P6tPw4ZF3iMXDgkGEzPcbdCannf';

const SEEN_KEY = 'pp_editor_guide_v1';

interface Step {
  want: string;
  how: string;
}

const STEPS: Step[] = [
  {
    want: '把表里的字段印上去',
    how: '点左边的字段名，直接落到画布上。关联字段点一下会生成一张明细表，子表有几行就印几行 —— 不用手动抓字段。',
  },
  {
    want: '加一个标题 / 表格 / 分栏',
    how: '在文字里敲 / 唤起插块菜单，选完插在当前区块后面。左边的组件栏是同一份东西，习惯哪个用哪个。',
  },
  {
    want: '改画布上的文字',
    how: '双击就地改。里面显示的是原始内容，{{客户名称}} 还是 {{客户名称}}，不会被写死成当前这条记录的值。',
  },
  {
    want: '合并单元格、加一行、删一列',
    how: '在表格上单击选格，Shift 单击拉出矩形选区，右键出菜单。按数据自动展开的明细行没有右键菜单 —— 那些行不属于模板，改了也存不住。',
  },
  {
    want: '看看印出来什么样',
    how: '顶栏切「预览」看真实数据，切回「编辑」看结构。确认没问题就回侧边栏打印 / 导出 PDF。',
  },
];

/** 常驻在画布上方的一行提示。引导关掉以后，它是唯一还在提醒这些快捷操作的东西。 */
export function CanvasHints({ onOpenGuide }: { onOpenGuide: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        padding: '5px 10px',
        marginBottom: 8,
        borderRadius: 7,
        background: 'var(--surface-2)',
        border: '1px solid var(--border-soft)',
        fontSize: 11.5,
        color: 'var(--ink-3)',
      }}
    >
      <span>
        敲 <kbd style={kbd}>/</kbd> 插块 · 双击改字 · 表格里单击选格、右键合并
      </span>
      <span style={{ flex: 1 }} />
      <Button type="link" size="small" style={{ fontSize: 11.5, padding: 0, height: 'auto' }} onClick={onOpenGuide}>
        怎么用
      </Button>
    </div>
  );
}

const kbd: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  border: '1px solid var(--border)',
  borderBottomWidth: 2,
  borderRadius: 4,
  padding: '0 4px',
  background: 'var(--surface)',
  color: 'var(--ink-2)',
};

export function EditorGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {HELP_DOC_URL && (
            <Button type="link" size="small" href={HELP_DOC_URL} target="_blank" rel="noreferrer" style={{ padding: 0 }}>
              完整帮助文档
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button type="primary" onClick={onClose}>
            开始排版
          </Button>
        </div>
      }
      title="画布怎么用"
      width={520}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 14 }}>
        五件事，看完就能排出第一份单据。随时可以从画布上方的「怎么用」重看。
      </Typography.Paragraph>
      {STEPS.map((s, i) => (
        <div key={s.want} style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'var(--accent)',
              color: 'var(--accent-ink)',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: 1,
            }}
          >
            {i + 1}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{s.want}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.7, marginTop: 2 }}>{s.how}</div>
          </div>
        </div>
      ))}
    </Modal>
  );
}

/**
 * 首次进编辑器自动弹一次，之后只在用户主动点「怎么用」时开。
 *
 * 记在 localStorage：清了缓存或换电脑会再弹一次，这没关系 —— 多看一次引导的
 * 代价，比一个老用户永远发现不了右键菜单要小。
 */
export function useEditorGuide(): { open: boolean; show: () => void; close: () => void } {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) {
        localStorage.setItem(SEEN_KEY, '1');
        setOpen(true);
      }
    } catch {
      // 无痕模式之类读不到存储：那就不自动弹，常驻提示条还在
    }
  }, []);
  return { open, show: () => setOpen(true), close: () => setOpen(false) };
}
